import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import api from '../../hooks/useApi';
import { formatMoney, formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';

const BUILD_LIMIT = 200;

export default function ProductSaleHistoryPage() {
  const { toast } = useToast();
  const [status, setStatus]       = useState(null);   // { hardware_remaining, resource_remaining, pending_review }
  const [building, setBuilding]   = useState(false);
  const [buildTotal, setBuildTotal] = useState(0);

  const [reviewRows, setReviewRows]   = useState([]);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewOffset, setReviewOffset]   = useState(0);
  const [choices, setChoices] = useState({}); // row.id -> { action, product_catalog_id?, new_description? }
  const [submittingReview, setSubmittingReview] = useState(false);

  const [products, setProducts] = useState([]);
  const [trends, setTrends]     = useState([]);
  const [trendSort, setTrendSort] = useState('variance');
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [productDetail, setProductDetail]     = useState(null);

  const loadStatus  = useCallback(() => api.get('product-catalog-sale-history/status').then(setStatus).catch(() => {}), []);
  const loadReview  = useCallback((offset = 0) => {
    setReviewLoading(true);
    api.get('product-catalog-sale-history/review', { offset, limit: 50 })
      .then(r => { setReviewRows(r.results || []); setReviewTotal(r.total || 0); setReviewOffset(offset); })
      .catch(() => setReviewRows([]))
      .finally(() => setReviewLoading(false));
  }, []);
  const loadTrends = useCallback((sort = trendSort) => {
    setTrendsLoading(true);
    api.get('product-catalog-sale-history/trends', { sort, limit: 100 })
      .then(setTrends).catch(() => setTrends([])).finally(() => setTrendsLoading(false));
  }, [trendSort]);

  useEffect(() => { loadStatus(); loadReview(0); loadTrends('variance'); api.get('product-catalog').then(setProducts).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runBuild = async () => {
    setBuilding(true);
    setBuildTotal(0);
    try {
      let remaining = Infinity;
      let total = 0;
      while (remaining > 0) {
        const r = await api.post('product-catalog-sale-history/build', { limit: BUILD_LIMIT });
        total += r.processed || 0;
        setBuildTotal(total);
        if ((r.hw_batch || 0) === 0 && (r.resource_batch || 0) === 0) break; // nothing left to process
        const s = await api.get('product-catalog-sale-history/status');
        setStatus(s);
        remaining = (s.hardware_remaining || 0) + (s.resource_remaining || 0);
      }
      toast.success(`Extracted ${total} sale record${total === 1 ? '' : 's'}.`);
      loadReview(0);
      loadTrends();
    } catch (err) {
      toast.error('Build failed: ' + (err.message || 'unknown error'));
    }
    setBuilding(false);
  };

  const setChoice = (id, patch) => setChoices(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const submitReview = async () => {
    const decisions = Object.entries(choices)
      .filter(([, c]) => c.action && !(c.action === 'pick' && !c.product_catalog_id))
      .map(([id, c]) => {
        if (c.action === 'create_new') {
          const row = reviewRows.find(r => r.id === id);
          return { id, action: 'create_new', new_product: { description: c.new_description || row?.item_description, category: row?.source_type === 'resource' ? 'Resource' : 'Hardware' } };
        }
        return { id, action: c.action === 'pick' ? 'confirm' : c.action, product_catalog_id: c.product_catalog_id };
      });
    if (!decisions.length) { toast.error('Make at least one decision first (or finish picking a product).'); return; }
    setSubmittingReview(true);
    try {
      const r = await api.post('product-catalog-sale-history/review', { decisions });
      const ok = r.results.filter(x => x.success).length;
      const fail = r.results.filter(x => !x.success).length;
      toast[fail ? 'error' : 'success'](`Applied ${ok} decision${ok === 1 ? '' : 's'}${fail ? `, ${fail} failed` : ''}.`);
      setChoices({});
      loadStatus();
      loadReview(0);
      loadTrends();
    } catch (err) {
      toast.error('Review submit failed: ' + (err.message || 'unknown error'));
    }
    setSubmittingReview(false);
  };

  const openProductDetail = (productCatalogId) => {
    if (expandedProduct === productCatalogId) { setExpandedProduct(null); return; }
    setExpandedProduct(productCatalogId);
    setProductDetail(null);
    api.get(`product-catalog/${productCatalogId}/sale-history`).then(setProductDetail).catch(() => setProductDetail([]));
  };

  const applyPrice = async (productCatalogId, price, label) => {
    if (!window.confirm(`Set this product's catalogue sell price to ${formatMoney(price)} (${label})?`)) return;
    try {
      await api.put(`product-catalog/${productCatalogId}`, { sell_price: price });
      toast.success('Catalogue sell price updated.');
      loadTrends();
    } catch (err) {
      toast.error('Update failed: ' + (err.message || 'unknown error'));
    }
  };

  const productOptions = useMemo(() => products.filter(p => p.is_active !== 0), [products]);

  return (
    <div className="fade-in p-4">
      <div className="mb-4">
        <h1 className="text-base font-extrabold text-navy">Product Sale History</h1>
        <div className="text-[9px] text-gray-500 mt-0.5">
          Extracts every hardware/resource line's actual sell price across every SOW already in this system — no PDF
          parsing needed, that data is already structured. Matches lines to the product catalogue, flags anything
          ambiguous for review, and reports price trends so the catalogue's sell price reflects reality.
        </div>
      </div>

      {/* Build */}
      <div className="bg-white border border-gray-200 rounded p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-bold text-navy">Step 1 — Extract &amp; match</div>
          <button onClick={runBuild} disabled={building}
            className="px-3 py-1.5 text-[10px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
            {building ? `Processing… (${buildTotal})` : 'Extract sale history'}
          </button>
        </div>
        {status && (
          <div className="text-[9px] text-gray-500 mt-2">
            {status.hardware_remaining + status.resource_remaining > 0
              ? `${status.hardware_remaining} hardware + ${status.resource_remaining} resource lines not yet extracted.`
              : 'All lines extracted.'}{' '}
            {status.pending_review > 0 && <span className="text-amber-600 font-semibold">{status.pending_review} awaiting your review below.</span>}
          </div>
        )}
      </div>

      {/* Review */}
      {reviewTotal > 0 && (
        <div className="bg-white border border-gray-200 rounded mb-4">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-amber-50/60">
            <div className="text-[9px] font-bold text-amber-700 uppercase">Needs review ({reviewTotal})</div>
            <button onClick={submitReview} disabled={submittingReview || !Object.keys(choices).length}
              className="px-2 py-1 text-[9px] font-bold bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-40">
              Apply {Object.keys(choices).length || ''} decision{Object.keys(choices).length === 1 ? '' : 's'}
            </button>
          </div>
          {reviewLoading ? <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div> : (
            <div className="overflow-x-auto"><table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">SOW</th>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Sold as</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Sell price</th>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Suggested match</th>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Decision</th>
                </tr>
              </thead>
              <tbody>
                {reviewRows.map(r => {
                  const choice = choices[r.id] || {};
                  return (
                    <tr key={r.id} className="border-b border-gray-100 align-top">
                      <td className="px-2 py-1.5 text-[9px] font-semibold text-navy whitespace-nowrap">{r.sow_reference}</td>
                      <td className="px-2 py-1.5 text-[9px] text-gray-600 max-w-[240px]">{r.item_description}</td>
                      <td className="px-2 py-1.5 text-[9px] text-right text-gray-700 whitespace-nowrap">{formatMoney(r.unit_sell)}</td>
                      <td className="px-2 py-1.5 text-[9px] text-gray-500 max-w-[220px]">
                        {r.candidate_code
                          ? <span>{r.candidate_code} — {r.candidate_description} <span className="text-gray-400">({Math.round((r.match_confidence || 0) * 100)}%)</span></span>
                          : <span className="text-gray-400">No match found</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex flex-col gap-1">
                          {r.candidate_code && (
                            <label className="flex items-center gap-1 text-[9px]">
                              <input type="radio" name={`action-${r.id}`} checked={choice.action === 'confirm'}
                                onChange={() => setChoice(r.id, { action: 'confirm', product_catalog_id: r.candidate_product_catalog_id })} />
                              Confirm match
                            </label>
                          )}
                          <label className="flex items-center gap-1 text-[9px]">
                            <input type="radio" name={`action-${r.id}`} checked={choice.action === 'pick'}
                              onChange={() => setChoice(r.id, { action: 'pick', product_catalog_id: '' })} />
                            <select className="form-input text-[9px] py-0.5" disabled={choice.action !== 'pick'}
                              value={choice.product_catalog_id || ''}
                              onChange={e => setChoice(r.id, { action: 'pick', product_catalog_id: e.target.value })}>
                              <option value="">Pick a different product…</option>
                              {productOptions.map(p => <option key={p.id} value={p.id}>{p.code} — {p.description}</option>)}
                            </select>
                          </label>
                          <label className="flex items-center gap-1 text-[9px]">
                            <input type="radio" name={`action-${r.id}`} checked={choice.action === 'create_new'}
                              onChange={() => setChoice(r.id, { action: 'create_new', new_description: r.item_description })} />
                            Create new catalogue item
                          </label>
                          <label className="flex items-center gap-1 text-[9px] text-gray-400">
                            <input type="radio" name={`action-${r.id}`} checked={choice.action === 'reject'}
                              onChange={() => setChoice(r.id, { action: 'reject' })} />
                            Not a catalogue item — leave unmatched
                          </label>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
          {reviewTotal > 50 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 text-[9px]">
              <button disabled={reviewOffset === 0} onClick={() => loadReview(Math.max(0, reviewOffset - 50))}
                className="px-2 py-1 border border-gray-200 rounded disabled:opacity-40">Prev</button>
              <span className="text-gray-400">{reviewOffset + 1}–{Math.min(reviewOffset + 50, reviewTotal)} of {reviewTotal}</span>
              <button disabled={reviewOffset + 50 >= reviewTotal} onClick={() => loadReview(reviewOffset + 50)}
                className="px-2 py-1 border border-gray-200 rounded disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      )}

      {/* Trends */}
      <div className="bg-white border border-gray-200 rounded">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
          <div className="text-[9px] font-bold text-navy uppercase">Sell-price trends by product</div>
          <div className="flex gap-1">
            {[['variance', 'Most inconsistent pricing'], ['volume', 'Most sold']].map(([v, l]) => (
              <button key={v} onClick={() => { setTrendSort(v); loadTrends(v); }}
                className={`px-2 py-1 text-[9px] font-semibold rounded border ${trendSort === v ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>{l}</button>
            ))}
          </div>
        </div>
        {trendsLoading ? <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div> : !trends.length ? (
          <div className="text-center py-8 text-[10px] text-gray-400">No confirmed matches yet — extract and review sale history first.</div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Product</th>
                <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Times sold</th>
                <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Min / Avg / Max</th>
                <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Distinct prices</th>
                <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Catalogue price</th>
                <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Last sold</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {trends.map(t => (
                <Fragment key={t.product_catalog_id}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => openProductDetail(t.product_catalog_id)}>
                    <td className="px-2 py-1.5 text-[9px] font-semibold text-navy">{t.code} <span className="text-gray-500 font-normal">— {t.description}</span></td>
                    <td className="px-2 py-1.5 text-[9px] text-right">{t.times_sold}</td>
                    <td className="px-2 py-1.5 text-[9px] text-right whitespace-nowrap">
                      {formatMoney(t.min_sell)} / {formatMoney(Math.round(t.avg_sell * 100) / 100)} / {formatMoney(t.max_sell)}
                    </td>
                    <td className={`px-2 py-1.5 text-[9px] text-right font-bold ${t.distinct_prices > 1 ? 'text-amber-600' : 'text-emerald-600'}`}>{t.distinct_prices}</td>
                    <td className="px-2 py-1.5 text-[9px] text-right text-gray-500">{t.catalog_sell_price ? formatMoney(t.catalog_sell_price) : '—'}</td>
                    <td className="px-2 py-1.5 text-[9px] text-gray-500 whitespace-nowrap">{formatDate(t.last_sold)}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      <button onClick={() => applyPrice(t.product_catalog_id, t.max_sell, 'most recent-week high')} className="text-[8px] font-semibold text-navy hover:underline mr-2">Set to max</button>
                      <button onClick={() => applyPrice(t.product_catalog_id, Math.round(t.avg_sell * 100) / 100, 'average sold')} className="text-[8px] font-semibold text-navy hover:underline">Set to avg</button>
                    </td>
                  </tr>
                  {expandedProduct === t.product_catalog_id && (
                    <tr className="bg-gray-50/60">
                      <td colSpan={7} className="px-4 py-2">
                        {!productDetail ? <div className="text-[9px] text-gray-400">Loading…</div> : (
                          <div className="overflow-x-auto"><table className="w-full">
                            <thead>
                              <tr className="text-[8px] uppercase text-gray-400 font-bold">
                                <th className="px-2 py-1 text-left">Date</th>
                                <th className="px-2 py-1 text-left">SOW</th>
                                <th className="px-2 py-1 text-right">Qty</th>
                                <th className="px-2 py-1 text-right">Unit sell</th>
                              </tr>
                            </thead>
                            <tbody>
                              {productDetail.map((d, i) => (
                                <tr key={i} className="border-t border-gray-100">
                                  <td className="px-2 py-1 text-[9px] text-gray-600">{formatDate(d.sale_date)}</td>
                                  <td className="px-2 py-1 text-[9px] text-navy font-semibold">{d.sow_reference}</td>
                                  <td className="px-2 py-1 text-[9px] text-right">{d.quantity}</td>
                                  <td className="px-2 py-1 text-[9px] text-right font-medium">{formatMoney(d.unit_sell)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table></div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
